import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { CellAlert } from '@liveline/ll_node_common/lib/models/websocket';
import { WebSocketState, LiveSignalData } from '../../types/websocket';

/**
 * A CellAlert as stored client-side: stamped with the browser ARRIVAL time.
 * Seen/unseen math must use received_ms, never the producer's time_stamp —
 * a plant-floor producer with a skewed clock would otherwise mint alerts
 * that arrive already "seen".
 */
export type StoredCellAlert = CellAlert & {
  received_ms: number;
  /**
   * How many times this same alert identity has folded into this row (1 = a
   * single occurrence, so it is left unset until the first repeat). A flapping
   * producer — a user alert whose value hovers at its threshold, or the
   * per-run-end "cleared on tracker cleanup" sweep — otherwise mints one row
   * per cycle and buries every other alert in the feed.
   */
  repeat_count?: number;
  /** time_stamp of the FIRST occurrence folded into this row. */
  first_time_stamp?: number;
  /**
   * A raise the producer never cleared, retired because its run ended (LT-3368).
   * Stored as state 'cleared' so every active/unseen count already excludes it, but
   * it must not render as a recovery: nothing was resolved, the run just stopped.
   */
  ended_with_run?: boolean;
};

export interface WebSocketSliceState {
  connections: {
    [facilityId: number]: WebSocketState;
  };
  liveSignalData: {
    [facilityId: number]: LiveSignalData;
  };
  cellAlerts: {
    [facilityId: number]: StoredCellAlert[];
  };
  /**
   * The latest RUN_STARTED / RUN_ENDED events per cell, kept apart from the feed so
   * that trimming the list can never lose the boundary a late raise is judged by.
   */
  cellAlertRunEvents: {
    [facilityId: number]: { [facilityLineId: number]: CellAlert[] };
  };
  globalError: string | null;
}

const initialState: WebSocketSliceState = {
  connections: {},
  liveSignalData: {},
  cellAlerts: {},
  cellAlertRunEvents: {},
  globalError: null
};

/** History rows kept per facility. Open raises are not counted against it. */
const CELL_ALERTS_MAX = 200;
/** Ceiling on open raises per facility, so a producer that never clears cannot grow the list forever. */
const CELL_ALERTS_OPEN_MAX = 500;
const RUN_EVENTS_PER_CELL = 4;

const hasRunId = (alert: CellAlert): boolean => alert.run_id != null && alert.run_id !== '';

const isRunBoundary = (alert: CellAlert): boolean =>
  alert.state === 'event' && (alert.code === 'RUN_STARTED' || alert.code === 'RUN_ENDED');

/**
 * Whether a run-boundary event ends an open raise (LT-3368). Producers do not clear
 * run-scoped conditions at a run boundary — loop-health retires them silently because
 * "RUN_ENDED already tells the story" — so without this they would read as active
 * forever. Only raises carrying a run_id are run-scoped; a cell-scoped raise waits for
 * its clear. Time-ordered so a replayed boundary of an EARLIER run never ends a later
 * run's raise. Keep in step with ll_websocket_server's cell-alerts-buffer.ts.
 */
export function runBoundaryRetires(event: CellAlert, raised: CellAlert): boolean {
  if (!isRunBoundary(event) || raised.state !== 'raised' || !hasRunId(raised)) return false;
  if (event.facility_line_id !== raised.facility_line_id) return false;
  if (event.code === 'RUN_ENDED') {
    return raised.run_id === event.run_id || raised.time_stamp <= event.time_stamp;
  }
  return raised.time_stamp <= event.time_stamp && (!hasRunId(event) || raised.run_id !== event.run_id);
}

/**
 * Fallback pairing for a clear that does NOT share its raise's alert_id. Every current
 * producer (ldcp, process_monitor) reuses the id, so this only serves one that mints a
 * fresh id; tag_id and alert_config_id narrow it when present, so one loop's recovery
 * cannot retire another loop's raise sharing the code. Same rule as the ws server.
 */
export function clearPairsWith(clear: CellAlert, raised: CellAlert): boolean {
  return (
    raised.state === 'raised' &&
    raised.source === clear.source &&
    raised.code === clear.code &&
    raised.facility_line_id === clear.facility_line_id &&
    (clear.tag_id == null || raised.tag_id === clear.tag_id) &&
    (clear.alert_config_id == null || raised.alert_config_id === clear.alert_config_id)
  );
}

/** How a row should read: its wire state, or 'ended' for a raise its run retired. */
export function alertDisplayState(alert: StoredCellAlert): 'raised' | 'cleared' | 'event' | 'ended' {
  return alert.ended_with_run ? 'ended' : alert.state;
}

/** Remember the latest run boundaries per cell from a batch of incoming alerts. */
export function rememberRunEvents(
  remembered: { [facilityLineId: number]: CellAlert[] } | undefined,
  incoming: CellAlert[],
): { [facilityLineId: number]: CellAlert[] } {
  const next = { ...(remembered ?? {}) };
  incoming.filter(isRunBoundary).forEach((event) => {
    next[event.facility_line_id] = [...(next[event.facility_line_id] ?? []), event].slice(-RUN_EVENTS_PER_CELL);
  });
  return next;
}

/**
 * Trim to CELL_ALERTS_MAX history rows, oldest first by time_stamp, without ever
 * dropping an open raise for history. The cap used to slice newest-first regardless of
 * state, so a busy facility pushed a still-raised condition out of the list.
 */
function trimCellAlerts(list: StoredCellAlert[]): StoredCellAlert[] {
  const open = list.filter((a) => a.state === 'raised');
  const history = list.filter((a) => a.state !== 'raised');
  if (history.length <= CELL_ALERTS_MAX && open.length <= CELL_ALERTS_OPEN_MAX) return list;
  const newestFirst = (rows: StoredCellAlert[], n: number) =>
    [...rows].sort((a, b) => b.time_stamp - a.time_stamp).slice(0, n);
  const keep = new Set([
    ...newestFirst(open, CELL_ALERTS_OPEN_MAX),
    ...newestFirst(history, CELL_ALERTS_MAX),
  ]);
  return list.filter((a) => keep.has(a));
}

/**
 * Identity of the underlying CONDITION, independent of the individual
 * occurrence. `alert_config_id` is the producer's own identity for a
 * user-configured alert and is preferred when present; message is the
 * fallback, which keeps distinct user alerts sharing one code apart (a cell
 * emits "Tension" and "Ovality" both as USER_ALERT_TRIGGERED).
 */
function alertIdentity(alert: StoredCellAlert): string {
  return [
    alert.facility_line_id,
    alert.source,
    alert.code,
    alert.alert_config_id ?? alert.message,
  ].join('|');
}

/**
 * Fold incoming alerts into the stored list (newest first).
 * - same alert_id replaces the existing entry (mute-duration reminders re-emit the
 *   same id — update in place, never stack duplicates). Every current producer also
 *   clears under the raise's id, so this is how a clear normally retires its raise.
 * - a 'cleared' whose id matched nothing retires the newest open raise it pairs with
 *   (clearPairsWith) — for a producer minting a fresh id for the clear. Only then:
 *   running it after an id match retired a SECOND raise sharing the code, so one
 *   loop's recovery hid another loop's still-raised alert (LT-3368).
 * - a repeat CLEAR of the same identity folds into that row with a repeat_count
 *   instead of stacking a new one, so a flapping condition costs one line rather
 *   than one line per cycle
 * - a raise its run has ended (runBoundaryRetires) is marked ended_with_run, whichever
 *   of the raise and the boundary arrived first. `runEvents` adds boundaries the list
 *   may already have trimmed.
 */
export function foldCellAlerts(
  existing: StoredCellAlert[],
  incoming: StoredCellAlert[],
  runEvents: CellAlert[] = [],
): StoredCellAlert[] {
  let list = [...existing];
  incoming.forEach((alert) => {
    const before = list.length;
    list = list.filter((a) => a.alert_id !== alert.alert_id);
    if (alert.state === 'cleared' && list.length === before) {
      const matchIdx = list.findIndex((a) => clearPairsWith(alert, a));
      if (matchIdx >= 0) {
        const open = list[matchIdx];
        list[matchIdx] = {
          ...open,
          state: 'cleared',
          time_stamp: alert.time_stamp,
          first_time_stamp: open.first_time_stamp ?? open.time_stamp,
        };
        return;
      }
    }
    // Repeat CLEAR of the same identity — fold in place, newest timestamp wins.
    // Scoped to 'cleared' on purpose:
    //   - 'raised' is an open condition: a distinct fact that keeps its own row
    //     (and its own severity styling).
    //   - 'event' is a point-in-time fact — RUN_STARTED / RUN_ENDED /
    //     PART_CHANGED. Consecutive runs of the same part produce an identical
    //     message ("Run started for part J1784"), so folding events would
    //     collapse four real runs into one line and destroy run history.
    if (alert.state === 'cleared') {
      const identity = alertIdentity(alert);
      const repeatIdx = list.findIndex(
        (a) => a.state === alert.state && !a.ended_with_run && alertIdentity(a) === identity
      );
      if (repeatIdx >= 0) {
        const prev = list[repeatIdx];
        const folded: StoredCellAlert = {
          ...prev,
          alert_id: alert.alert_id,
          time_stamp: alert.time_stamp,
          received_ms: alert.received_ms,
          message: alert.message,
          repeat_count: (prev.repeat_count ?? 1) + 1,
          first_time_stamp: prev.first_time_stamp ?? prev.time_stamp,
        };
        // Re-seat at the top: it is the most recent thing that happened.
        list.splice(repeatIdx, 1);
        list.unshift(folded);
        return;
      }
    }
    list.unshift(alert);
  });
  const boundaries = [...runEvents, ...list.filter(isRunBoundary)];
  if (boundaries.length > 0) {
    list = list.map((a): StoredCellAlert => {
      const ending = boundaries.find((event) => runBoundaryRetires(event, a));
      // Stamped with the boundary, as a clear stamps its row: a raise hours older than
      // the history around it would otherwise be the oldest row the moment it stopped
      // being open, and the history trim would drop it before anyone saw how it ended.
      return ending
        ? {
            ...a,
            state: 'cleared',
            ended_with_run: true,
            time_stamp: Math.max(a.time_stamp, ending.time_stamp),
            first_time_stamp: a.first_time_stamp ?? a.time_stamp,
          }
        : a;
    });
  }
  return trimCellAlerts(list);
}

const websocketSlice = createSlice({
  name: 'websocket',
  initialState,
  reducers: {
    setConnectionState: (state, action: PayloadAction<{ facilityId: number; state: WebSocketState }>) => {
      const { facilityId, state: connectionState } = action.payload;
      if (connectionState.isConnected) {
        state.globalError = null;
      }
      state.connections[facilityId] = connectionState;
    },
    
    setLiveSignalData: (state, action: PayloadAction<{ facilityId: number; data: LiveSignalData }>) => {
      const { facilityId, data } = action.payload;
      state.liveSignalData[facilityId] = data;
    },
    
    clearLiveSignalData: (state, action: PayloadAction<number>) => {
      const facilityId = action.payload;
      delete state.liveSignalData[facilityId];
    },
    
    removeConnection: (state, action: PayloadAction<number>) => {
      const facilityId = action.payload;
      delete state.connections[facilityId];
      delete state.liveSignalData[facilityId];
    },
    
    /**
     * Live pushes and snapshot replays alike FOLD into the list — never replace it.
     * The ws server sends one snapshot per CellAlerts topic, and a facility subscribes
     * to every cell's topic (twice, with OnPrem.), so a replacing snapshot kept only
     * whichever topic arrived last and silently dropped every other cell's raised
     * alerts on each reconnect (LT-3368). Replays the client already holds are
     * filtered out before dispatch (takeNewCellAlerts in useCellAlerts).
     */
    addCellAlerts: (state, action: PayloadAction<{ facilityId: number; alerts: CellAlert[] }>) => {
      const { facilityId, alerts } = action.payload;
      const receivedMs = Date.now();
      const stamped: StoredCellAlert[] = alerts.map((a) => ({ ...a, received_ms: receivedMs }));
      const runEvents = rememberRunEvents(state.cellAlertRunEvents[facilityId], alerts);
      state.cellAlertRunEvents[facilityId] = runEvents;
      state.cellAlerts[facilityId] = foldCellAlerts(
        state.cellAlerts[facilityId] ?? [],
        stamped,
        Object.values(runEvents).flat(),
      );
    },

    clearCellAlerts: (state, action: PayloadAction<number>) => {
      delete state.cellAlerts[action.payload];
      delete state.cellAlertRunEvents[action.payload];
    },

    setGlobalError: (state, action: PayloadAction<string | null>) => {
      state.globalError = action.payload;
    },

    clearAllConnections: (state) => {
      state.connections = {};
      state.liveSignalData = {};
      state.cellAlerts = {};
      state.cellAlertRunEvents = {};
      state.globalError = null;
    }
  }
});

export const {
  setConnectionState,
  setLiveSignalData,
  clearLiveSignalData,
  removeConnection,
  addCellAlerts,
  clearCellAlerts,
  setGlobalError,
  clearAllConnections
} = websocketSlice.actions;

export default websocketSlice.reducer; 